import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '../context/AuthContext';

/** How many recently opened events are remembered. */
export const RECENT_EVENTS_LIMIT = 5;

export interface RecentEvent {
  id: string;
  name: string;
  /** ISO time it was last opened. */
  visitedAt: string;
}

const CHANGE = 'recent-events-change';

// Per person: an administrator impersonating a planner, or two people sharing
// a machine, each keep their own list.
const storageKey = (userId: string) => `recent-events:${userId}`;

export function readRecentEvents(userId: string | undefined): RecentEvent[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is RecentEvent => !!e && typeof e.id === 'string' && typeof e.name === 'string',
      )
      .slice(0, RECENT_EVENTS_LIMIT);
  } catch {
    return [];
  }
}

/** Puts an event at the top of the list (moving it if it is already there). */
export function recordRecentEvent(
  userId: string | undefined,
  event: { id: string; name: string },
  now = new Date(),
): void {
  if (!userId) return;
  const next = [
    { id: event.id, name: event.name, visitedAt: now.toISOString() },
    ...readRecentEvents(userId).filter((e) => e.id !== event.id),
  ].slice(0, RECENT_EVENTS_LIMIT);
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode, quota): the list just is not kept.
  }
  window.dispatchEvent(new Event(CHANGE));
}

/** Drops an event that no longer opens (deleted, or access removed). */
export function forgetRecentEvent(userId: string | undefined, eventId: string): void {
  if (!userId) return;
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify(readRecentEvents(userId).filter((e) => e.id !== eventId)),
    );
  } catch {
    // Nothing to do.
  }
  window.dispatchEvent(new Event(CHANGE));
}

function subscribe(onChange: () => void) {
  window.addEventListener(CHANGE, onChange);
  // Another tab opening an event.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * The events this person opened most recently, newest first — kept in this
 * browser only. Feeds the sidebar's "Recent" list and the quick search.
 */
export function useRecentEvents() {
  const { user } = useAuth();
  const userId = user?.id;
  // The snapshot must be stable between renders, so compare by content.
  const raw = useSyncExternalStore(subscribe, () => {
    try {
      return userId ? (localStorage.getItem(storageKey(userId)) ?? '') : '';
    } catch {
      return '';
    }
  });
  const events = raw === '' ? [] : readRecentEvents(userId);
  const record = useCallback(
    (event: { id: string; name: string }) => recordRecentEvent(userId, event),
    [userId],
  );
  const forget = useCallback((eventId: string) => forgetRecentEvent(userId, eventId), [userId]);
  return { events, record, forget };
}
